import { functions } from '../../firebase';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { Check, X } from 'lucide-react';

export const GoogleOAuthCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const exchangeAttempted = useRef(false);

    useEffect(() => {
        const code = searchParams.get('code');
        const error = searchParams.get('error');

        if (error) {
            setStatus('error');
            setErrorMessage(`Google authentication failed: ${error}`);
            return;
        }

        if (!code) {
            setStatus('error');
            setErrorMessage('No authorization code returned from Google.');
            return;
        }

        if (exchangeAttempted.current) return;
        exchangeAttempted.current = true;

        const exchangeCode = async () => {
            try {
                const googleExchangeToken = httpsCallable(functions, 'googleExchangeToken');
                
                await googleExchangeToken({ code });
                
                setStatus('success');
                // Redirect back to where they were
                setTimeout(() => {
                    const returnUrl = sessionStorage.getItem('gcImportPending');
                    sessionStorage.removeItem('gcImportPending');
                    
                    if (returnUrl) {
                        const urlObj = new URL(returnUrl, window.location.origin);
                        urlObj.searchParams.set('openGCImport', 'true');
                        navigate(urlObj.pathname + urlObj.search, { replace: true });
                    } else {
                        navigate('/app', { replace: true });
                    }
                }, 2000);
            } catch (err: any) {
                console.error("Token Exchange Error:", err);
                setStatus('error');
                setErrorMessage(err.message || 'Failed to link account.');
            }
        };

        exchangeCode();
    }, [searchParams, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 font-sans p-6 text-gray-800 dark:text-gray-200">
            {status === 'loading' && (
                <div className="flex flex-col items-center space-y-4 text-center">
                    <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-2"></div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Connecting your Google Classroom to your PocketProf</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Securing your connection tokens. This will only take a moment...
                    </p>
                </div>
            )}

            {status === 'success' && (
                <div className="flex flex-col items-center space-y-4 text-center">
                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center text-3xl">
                        <Check size={32} />
                    </div>
                    <h2 className="text-xl font-bold text-emerald-600 dark:text-emerald-400">Successfully Connected!</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Your Google Classroom is connected. Redirecting you back to PocketProf...
                    </p>
                </div>
            )}

            {status === 'error' && (
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl max-w-md w-full border border-gray-200 dark:border-gray-700 text-center space-y-6">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto text-3xl">
                        <X size={32} />
                    </div>
                    <h2 className="text-xl font-bold text-red-600 dark:text-red-400">Connection Failed</h2>
                    <p className="text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-950/20 p-3 rounded-lg border border-red-200 dark:border-red-900/30">
                        {errorMessage}
                    </p>
                    <button
                        onClick={() => navigate('/app')}
                        className="px-5 py-2.5 bg-primary hover:bg-primary/95 text-white rounded-xl shadow-md transition-colors"
                    >
                        Return to Dashboard
                    </button>
                </div>
            )}
        </div>
    );
};
